import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Booking, BookingStatus } from "src/business/booking";
import { Deal } from "src/business/deal";
import { DayHoursList, HourObjectType } from "src/business/restaurant/restaurant";
import { findDealForSlot } from "./dealHelpers";

// The restaurant mapper builds its schedule times with `dayjs("HH:mm", "HH:mm")`,
// which only parses once customParseFormat is registered. MUI's date adapter
// happens to register it as a side effect, but relying on that import order is
// brittle — register it here so this module is correct on its own.
dayjs.extend(customParseFormat);

export const SLOT_INTERVAL_MINUTES = 30;
// The grid always covers a whole day, 00:00 → 23:30, independent of the
// restaurant's opening hours: a booking may be placed on any of these rows.
export const SLOTS_PER_DAY = 48;

const pad = (value: number): string => String(value).padStart(2, "0");

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/** "HH:mm" shown on a grid row. Rows outside the day wrap, so row 48 reads 00:00. */
export const slotLabelForRow = (row: number): string => {
  const normalized = ((row % SLOTS_PER_DAY) + SLOTS_PER_DAY) % SLOTS_PER_DAY;
  const minutes = normalized * SLOT_INTERVAL_MINUTES;
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
};

/** Grid row containing a "HH:mm" time. Off-grid minutes floor onto their row. */
export const rowForSlotLabel = (label: string): number => {
  const [hours, minutes] = label.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;
  return Math.floor((hours * 60 + minutes) / SLOT_INTERVAL_MINUTES);
};

/** How many rows a booking covers, from the restaurant's booking duration. */
export const rowSpanForDuration = (bookingDuration: number): number =>
  Math.max(1, Math.round(bookingDuration / SLOT_INTERVAL_MINUTES));

/** "90" → "1h30", "120" → "2h", "30" → "30min". */
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}min`;
  return rest === 0 ? `${hours}h` : `${hours}h${pad(rest)}`;
};

// ─── Opening hours ───────────────────────────────────────────────────────────

/** A stretch of open rows; `endRow` is exclusive. */
export interface OpenWindow {
  startRow: number;
  endRow: number;
}

// dayjs' `day()` is 0-indexed on Sunday. Matching on this rather than on a
// formatted name keeps the lookup working whatever locale dayjs is set to.
const WEEKDAY_NAMES = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];

const rowForTime = (time: Dayjs): number | null =>
  time.isValid() ? rowForSlotLabel(time.format("HH:mm")) : null;

const windowForService = (service: HourObjectType | null): OpenWindow | null => {
  if (!service) return null;
  const startRow = rowForTime(service.start);
  if (startRow === null) return null;
  const parsedEnd = rowForTime(service.end);
  // A zero-length service is the mapper's sentinel for a closed day (00:00 to
  // 00:00), not a service — the schedule payload filters those out the same way.
  if (parsedEnd === startRow) return null;
  // An end before the start closes at midnight: the API sends "24:00", which
  // parses back to 00:00, so the service runs to the end of the day.
  const endRow =
    parsedEnd === null || parsedEnd < startRow ? SLOTS_PER_DAY : parsedEnd;
  return { startRow, endRow };
};

/**
 * Open rows for a date. Purely informational: the grid tints these, but nothing
 * stops a booking being placed outside them — opening hours change and leaves
 * get added after the fact, so they must never gate what is already booked.
 */
export const openWindowsForDate = (
  schedule: DayHoursList,
  date: Dayjs,
  leaves: string[],
): OpenWindow[] => {
  if (leaves.some((leave) => dayjs(leave).isSame(date, "day"))) return [];
  const dayName = WEEKDAY_NAMES[date.day()];
  const entry = schedule.find((hour) => hour.day.toUpperCase() === dayName);
  if (!entry) return [];
  return [windowForService(entry.morning), windowForService(entry.evening)]
    .filter((window): window is OpenWindow => window !== null)
    .filter((window) => window.endRow > window.startRow);
};

export const isRowOpen = (windows: OpenWindow[], row: number): boolean =>
  windows.some((window) => row >= window.startRow && row < window.endRow);

// ─── Booking segments ────────────────────────────────────────────────────────

/**
 * One booking as drawn on the selected day. A booking crossing midnight yields a
 * segment on each day it touches, but both carry the same `booking`, so moving,
 * inspecting or cancelling from either acts on the one underlying booking.
 */
export interface BookingSegment {
  booking: Booking;
  table: number;
  // Rows relative to the selected day's 00:00. Negative when the booking began
  // on an earlier day, which is what makes the spill-over maths fall out.
  absStartRow: number;
  totalRows: number;
  // Clipped to the visible day.
  startRow: number;
  endRow: number;
  startsBeforeToday: boolean;
  endsAfterToday: boolean;
  // Share of the column when bookings collide on one table.
  lane: number;
  laneCount: number;
}

/** A cancelled booking has left the floor plan. */
export const isOnBoard = (booking: Booking): boolean =>
  booking.bookingStatus !== BookingStatus.CANCELED;

/**
 * Overlaps are possible now that placement is unconstrained, so bookings sharing
 * rows on a table split the column between them the way a calendar does — no
 * booking is ever hidden behind another.
 */
const assignLanes = (segments: BookingSegment[]): BookingSegment[] => {
  const byTable = new Map<number, BookingSegment[]>();
  segments.forEach((segment) => {
    const existing = byTable.get(segment.table) ?? [];
    existing.push(segment);
    byTable.set(segment.table, existing);
  });

  const placed: BookingSegment[] = [];
  byTable.forEach((tableSegments) => {
    const ordered = [...tableSegments].sort(
      (left, right) => left.startRow - right.startRow || left.endRow - right.endRow,
    );

    let cluster: BookingSegment[] = [];
    let clusterEnd = -1;

    const flushCluster = () => {
      const laneEnds: number[] = [];
      cluster.forEach((segment) => {
        const free = laneEnds.findIndex((end) => end <= segment.startRow);
        const lane = free === -1 ? laneEnds.length : free;
        laneEnds[lane] = segment.endRow;
        segment.lane = lane;
      });
      cluster.forEach((segment) => {
        segment.laneCount = laneEnds.length;
      });
      placed.push(...cluster);
      cluster = [];
      clusterEnd = -1;
    };

    ordered.forEach((segment) => {
      if (cluster.length > 0 && segment.startRow >= clusterEnd) flushCluster();
      cluster.push(segment);
      clusterEnd = Math.max(clusterEnd, segment.endRow);
    });
    if (cluster.length > 0) flushCluster();
  });

  return placed;
};

/**
 * Lay out every booking touching `selectedDate`, including the tail of one that
 * started the day before. Callers must therefore load bookings from the previous
 * day too.
 */
export const buildDaySegments = (
  bookings: Booking[],
  selectedDate: Dayjs,
  totalRows: number,
): BookingSegment[] => {
  const dayStart = selectedDate.startOf("day");
  const segments: BookingSegment[] = [];

  bookings.forEach((booking) => {
    if (booking.assignedTable === null || !isOnBoard(booking)) return;

    const offsetDays = dayjs(booking.bookingDate)
      .startOf("day")
      .diff(dayStart, "day");
    const absStartRow =
      offsetDays * SLOTS_PER_DAY + rowForSlotLabel(booking.bookingHour);
    const absEndRow = absStartRow + totalRows;
    if (absEndRow <= 0 || absStartRow >= SLOTS_PER_DAY) return;

    segments.push({
      booking,
      table: booking.assignedTable,
      absStartRow,
      totalRows,
      startRow: Math.max(0, absStartRow),
      endRow: Math.min(SLOTS_PER_DAY, absEndRow),
      startsBeforeToday: absStartRow < 0,
      endsAfterToday: absEndRow > SLOTS_PER_DAY,
      lane: 0,
      laneCount: 1,
    });
  });

  return assignLanes(segments);
};

/**
 * The deal or event covering each row of the day, indexed by row. Resolved once
 * per day rather than per cell, since offers apply to every table alike.
 */
export const dealsByRow = (
  deals: Deal[],
  selectedDate: Dayjs,
): (Deal | undefined)[] => {
  const dateKey = selectedDate.format("YYYY-MM-DD");
  return Array.from({ length: SLOTS_PER_DAY }, (_, row) =>
    findDealForSlot(deals, dateKey, slotLabelForRow(row)),
  );
};

/**
 * Bookings still waiting for a table on the selected day.
 *
 * The day filter is not redundant: the board deliberately over-fetches the day
 * either side so a booking running past midnight has its tail available, and
 * without it the tray listed the neighbouring days' unassigned bookings too — the
 * same booking appearing on three consecutive days. `isSame(…, "day")` compares
 * calendar fields, so it also stays correct across a DST boundary, where a
 * millisecond `diff` in days floors to the wrong number.
 */
export const getUnassignedBookings = (
  bookings: Booking[],
  selectedDate: Dayjs,
): Booking[] =>
  bookings.filter(
    (booking) =>
      booking.assignedTable === null &&
      isOnBoard(booking) &&
      dayjs(booking.bookingDate).isSame(selectedDate, "day"),
  );

// ─── Day view ────────────────────────────────────────────────────────────────

/** A contiguous slice of the day's rows; `endRow` is exclusive. */
export interface DayView {
  startRow: number;
  endRow: number;
}

/** The whole day, 00:00 → 23:30 — the grid's fallback extent. */
export const FULL_DAY_VIEW: DayView = { startRow: 0, endRow: SLOTS_PER_DAY };

/**
 * The tightest run of rows that still covers every booking, open service and
 * deal/event on the selected day. The grid trims the dead hours off its top and
 * bottom with this, so the rows that carry something get the whole height rather
 * than sharing it with a closed night. Falls back to the whole day when nothing
 * is on the board, so an empty schedule never collapses to nothing.
 */
export const activeDayView = (
  segments: BookingSegment[],
  openWindows: OpenWindow[],
  dealRows: (Deal | undefined)[],
): DayView => {
  const rows: number[] = [];
  openWindows.forEach((window) => rows.push(window.startRow, window.endRow - 1));
  dealRows.forEach((deal, row) => {
    if (deal) rows.push(row);
  });
  segments.forEach((segment) => rows.push(segment.startRow, segment.endRow - 1));

  const inDay = rows.filter((row) => row >= 0 && row < SLOTS_PER_DAY);
  if (inDay.length === 0) return FULL_DAY_VIEW;

  return { startRow: Math.min(...inDay), endRow: Math.max(...inDay) + 1 };
};

const viewRowCount = (view: DayView): number => view.endRow - view.startRow;

/** A row's distance from the top of the view, as a CSS percentage. */
export const viewTopPercent = (row: number, view: DayView): string =>
  `${((row - view.startRow) / viewRowCount(view)) * 100}%`;

/** A span of rows as a share of the view's height, as a CSS percentage. */
export const viewLengthPercent = (rows: number, view: DayView): string =>
  `${(rows / viewRowCount(view)) * 100}%`;

// ─── Moving ──────────────────────────────────────────────────────────────────

/**
 * Row a dragged booking starts on once dropped: the row under the pointer, less
 * the offset inside the block that was grabbed, kept within the visible day so a
 * booking always starts on the day you dropped it on.
 */
export const resolveDropStartRow = (
  pointerRow: number,
  grabOffsetRows: number,
): number => clamp(pointerRow - grabOffsetRows, 0, SLOTS_PER_DAY - 1);

// ─── Table occupancy ─────────────────────────────────────────────────────────

/** The stretch of rows a booking holds on its table, relative to a reference day. */
export interface TableInterval {
  bookingId: string;
  table: number;
  start: number;
  end: number;
}

/**
 * Every seated booking as an interval in rows relative to `referenceDate`'s
 * 00:00 — negative for earlier days, past `SLOTS_PER_DAY` for later ones — so
 * bookings on neighbouring days are comparable with today's.
 */
export const bookingIntervals = (
  bookings: Booking[],
  referenceDate: Dayjs,
  totalRows: number,
): TableInterval[] => {
  const dayStart = referenceDate.startOf("day");
  const intervals: TableInterval[] = [];

  bookings.forEach((booking) => {
    if (booking.assignedTable === null || !isOnBoard(booking)) return;
    const offsetDays = dayjs(booking.bookingDate)
      .startOf("day")
      .diff(dayStart, "day");
    const start =
      offsetDays * SLOTS_PER_DAY + rowForSlotLabel(booking.bookingHour);
    intervals.push({
      bookingId: booking.id,
      table: booking.assignedTable,
      start,
      end: start + totalRows,
    });
  });

  return intervals;
};

/**
 * A table seats one booking at a time, so a candidate placement is refused when
 * it overlaps anything already on that table. `ignoreBookingId` excludes the
 * booking being moved from colliding with itself.
 */
export const hasTableConflict = (
  intervals: TableInterval[],
  table: number,
  start: number,
  end: number,
  ignoreBookingId?: string,
): boolean =>
  intervals.some(
    (interval) =>
      interval.table === table &&
      interval.bookingId !== ignoreBookingId &&
      start < interval.end &&
      interval.start < end,
  );

export const tableColumns = (numberOfTables: number): number[] =>
  Array.from({ length: numberOfTables }, (_, index) => index + 1);
